import { derived, get, writable } from "svelte/store"
import {
  fetchActiveCustomerImportCommit,
  type CustomerImportActiveStatus,
} from "../../../../api/kehrblatt/imports"

export type CustomerImportStatusState = {
  organizationId: string | null
  status: CustomerImportActiveStatus | null
  loading: boolean
  error: string | null
  loadedAt: number | null
}

const normalizeOrganizationId = (organizationId?: string | null) => {
  const normalized = (organizationId ?? "").trim()

  return normalized || null
}

const initialState = (organizationId: string | null = null): CustomerImportStatusState => ({
  organizationId,
  status: null,
  loading: false,
  error: null,
  loadedAt: null,
})

export const customerImportStatusOrganizationId = writable<string | null>(null)
export const customerImportStatus = writable<CustomerImportStatusState>(initialState())
export const activeCustomerImport = derived(customerImportStatus, ($state) =>
  $state.status?.active ? $state.status : null,
)
export const customerImportLocked = derived(activeCustomerImport, ($active) =>
  Boolean($active?.active),
)

let inflight: {
  organizationId: string
  promise: Promise<CustomerImportActiveStatus | null>
} | null = null

export const setCustomerImportStatusOrganizationId = (organizationId?: string | null) => {
  const normalized = normalizeOrganizationId(organizationId)
  customerImportStatusOrganizationId.set(normalized)
  customerImportStatus.update((prev) =>
    prev.organizationId === normalized ? prev : initialState(normalized),
  )
}

export const refreshCustomerImportStatus = async (
  organizationId?: string | null,
): Promise<CustomerImportActiveStatus | null> => {
  const resolvedOrganizationId =
    normalizeOrganizationId(organizationId) ?? get(customerImportStatusOrganizationId)

  if (!resolvedOrganizationId) {
    resetCustomerImportStatus(null)

    return null
  }

  if (inflight?.organizationId === resolvedOrganizationId) return inflight.promise

  const request = {
    organizationId: resolvedOrganizationId,
    promise: (async () => {
      customerImportStatus.update((prev) => ({
        ...prev,
        organizationId: resolvedOrganizationId,
        loading: true,
        error: null,
      }))
      try {
        const status = await fetchActiveCustomerImportCommit(resolvedOrganizationId)

        if (get(customerImportStatusOrganizationId) === resolvedOrganizationId) {
          customerImportStatus.set({
            organizationId: resolvedOrganizationId,
            status,
            loading: false,
            error: null,
            loadedAt: Date.now(),
          })
        }

        return status
      } catch (err) {
        console.error("customer_import_active_status_failed", err)

        if (get(customerImportStatusOrganizationId) === resolvedOrganizationId) {
          customerImportStatus.update((prev) => ({
            ...prev,
            organizationId: resolvedOrganizationId,
            loading: false,
            error: "Importstatus konnte nicht geladen werden.",
          }))
        }

        return get(customerImportStatus).status
      } finally {
        if (inflight === request) {
          inflight = null
        }
      }
    })(),
  }
  inflight = request

  return request.promise
}

export const resetCustomerImportStatus = (organizationId?: string | null) => {
  customerImportStatus.set(initialState(normalizeOrganizationId(organizationId)))
}
